// Офлайн-кеш на IndexedDB (SPEC §4.6). Отдельная БД от outbox-очереди транспорта (TOPOLOGY).
// Один object-store `snapshot` с единственной записью под ключом SNAPSHOT_KEY — стор кеширует
// выборочную проекцию (snapshot.ts), а не дельты. В тестах подменяется fake-indexeddb (global
// indexedDB) или любым in-memory CacheStore.
const DB_NAME = 'tele-chat-client-store';
const STORE_NAME = 'snapshot';
const SNAPSHOT_KEY = 'current';
const DB_VERSION = 1;
function reqAsPromise(req) {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
    });
}
function openDb(factory) {
    return new Promise((resolve, reject) => {
        const req = factory.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    });
}
/**
 * IndexedDB-кеш. `factory` инъецируется (дефолт — глобальный `indexedDB`); в тестах сюда уходит
 * fake-indexeddb. Бросает явную ошибку, если IndexedDB недоступен и не передан factory.
 */
export function createIndexedDbCache(factory) {
    const idb = factory ??
        (typeof indexedDB !== 'undefined' ? indexedDB : undefined);
    if (!idb) {
        throw new Error('IndexedDB недоступен в этой среде. Передайте factory (например, fake-indexeddb) ' +
            'или используйте createMemoryCache() для тестов.');
    }
    let dbPromise = null;
    function db() {
        if (!dbPromise)
            dbPromise = openDb(idb);
        return dbPromise;
    }
    return {
        async load() {
            const database = await db();
            const tx = database.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const value = await reqAsPromise(store.get(SNAPSHOT_KEY));
            return value ?? null;
        },
        async save(s) {
            const database = await db();
            const tx = database.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            await reqAsPromise(store.put(s, SNAPSHOT_KEY));
            await new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error ?? new Error('IndexedDB save failed'));
                tx.onabort = () => reject(tx.error ?? new Error('IndexedDB save aborted'));
            });
        },
        async clear() {
            const database = await db();
            const tx = database.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            await reqAsPromise(store.delete(SNAPSHOT_KEY));
            await new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error ?? new Error('IndexedDB clear failed'));
                tx.onabort = () => reject(tx.error ?? new Error('IndexedDB clear aborted'));
            });
        },
    };
}
/**
 * In-memory кеш — для тестов/SSR без IndexedDB. Снапшот клонируется (structuredClone), чтобы
 * имитировать сериализацию IndexedDB (никаких живых ссылок между store и кешем).
 */
export function createMemoryCache() {
    let snap = null;
    const clone = (s) => typeof structuredClone === 'function'
        ? structuredClone(s)
        : JSON.parse(JSON.stringify(s));
    return {
        async load() {
            return snap ? clone(snap) : null;
        },
        async save(s) {
            snap = clone(s);
        },
        async clear() {
            snap = null;
        },
    };
}
//# sourceMappingURL=indexeddb.js.map